import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const MainPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [inputData, setInputData] = useState([]);
  const [layers, setLayers] = useState([]);
  const [trajectoryCards, setTrajectoryCards] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    setLoading(true);
    setError(null);
  
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
        setError(data.message || "Failed to fetch CMS pages");
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
      setError(err.response?.data?.message || "Failed to load CMS pages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 100 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setInputData(allItems.filter(item => item.type === 'input_data'));
        setLayers(allItems.filter(item => item.type === 'layer'));
        setTrajectoryCards(allItems.filter(item => item.type === 'trajectory_card'));
        setComparisonData(allItems.filter(item => item.type === 'comparison'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <div>
      <Helmet>
        <title>The Predyxiq™ Clinical Intelligence Report</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 26 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "40px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-3" style={{ lineHeight: "43px" }}>
            {cms[26]?.cms_page_heading}
            <br />
            <span>{cms[26]?.cms_page_small_description}</span>
          </h2>

          {parse(cms[26]?.cms_page_description || "")}
          <a href="#" className="action-items-primary-btn mr-2">
            View Sample Report
            <i className="fas fa-chevron-right" />
          </a>
          <a href="#" className="action-items-primary-btn">
            Start Your Quick Assessment
            <i className="fas fa-chevron-right" />
          </a>

          <img
            src={`${apiBaseUrl}/assets/images/${cms[26]?.cms_image}`}
            alt="About us"
            className="img-fluid"
            style={{ marginTop: "20px", borderRadius: "10px" }}
          />
        </div>
      </section>

      {/* ============================================ */}
      {/* TRANSPARENT SECTION - CMS ID 27 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[27]?.cms_page_heading || "Transparent <span>by Design</span>")}
              <br />
              <span>{cms[27]?.cms_page_small_description}</span>
            </h2>
          </div>

          <div className="predyx-content-box">
            {parse(cms[27]?.cms_page_description || "")}
          </div>

          <div className="predyx-process-section mt-5">
            <h3 className="predyx-process-title text-center">
              {cms[28]?.cms_page_heading}
            </h3>

            <p className="predyx-process-subtitle text-center">
              {cms[28]?.cms_page_small_description}
            </p>

            <div className="row justify-content-center">
              {trajectoryCards.map((item, index) => (
                <div className="col-md-3 mb-4" key={index}>
                  <div className="predyx-step-card">
                    <div className="predyx-step-content">
                      <h5>{item.title}</h5>
                      <p>{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="predyx-quote-section mt-3">
            <blockquote>
              “Every organ tells part of the story. Clinical Intelligence
              emerges when those stories are interpreted together.”
            </blockquote>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* INPUT DATA SECTION - Items (type: input_data) */}
      {/* ============================================ */}
      <section className="predyx2-section">
        <div className="container">
          <div className="predyx2-heading text-center mb-5">
            <h2 className="predyx2-title mt-2">
              What every clinical <span>Intelligence report Includes</span>
            </h2>
            <h4 className="predyx2-subtitle mt-2">
              Every report follows the same transparent structure. Designed for
              consistency. Built for clarity.
            </h4>
          </div>

          <div className="row">
            {inputData.map((item, index) => (
              <div className="col-lg-4 mb-4" key={index}>
                <div className="predyx2-card">
                  <div className="predyx2-card-content">
                    <h5>{item.title}</h5>
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* LAYERS SECTION - Items (type: layer) */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
            {parse(cms[30]?.cms_page_heading || "")}
            </h2>
            <h4 className="predyx-sub-title mt-2">
            {parse(cms[30]?.cms_page_small_description || "")}
            </h4>
            <h3 className="hero-title">"Why?"</h3>
          </div>

          <div className="predyx-content-box">
            <div className="info-card" style={{ padding: "0px" }}>
            {parse(cms[30]?.cms_page_description || "")}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ONE REPORT TWO AUDIENCES - CMS ID 29 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#fff" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[32]?.cms_page_heading || "One Report. <span>Two Audiences.</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[32]?.cms_page_small_description}
            </p>
          </div>

          <div className="predyx3-timeline mt-5">
            {parse(cms[32]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* REPORT EVOLVES WITH YOU - CMS ID 30 */}
      {/* ============================================ */}
      <section className="predyx7-section py-5" style={{ background: "#eaf0f6" }}>
        <div className="container">
          <div className="predyx7-header mb-5">
            <h2 className="predyx7-title text-center">
              {parse(cms[33]?.cms_page_heading || "A Report that <span>evolves with you</span>")}
            </h2>
            <p className="predyx3-description mt-2 text-center">
              {cms[33]?.cms_page_small_description}
            </p>
          </div>

          <div className="predyx7-card">
            {parse(cms[33]?.cms_page_description || "")}

            <div className="predyx7-flow mt-5">
              <div className="predyx7-step">Assessment 1</div>

              <div className="predyx7-arrow">&#10140;</div>

              <div className="predyx7-step predyx7-active">Assessment 2</div>

              <div className="predyx7-arrow">&#10140;</div>

              <div className="predyx7-step">Assessment 3</div>
            </div>

            <div className="predyx7-down-arrow">&#8595;</div>

            <div className="predyx7-trajectory">
              Longitudinal Organ Trajectories
            </div>
            <div className="predyx7-down-arrow">&#8595;</div>
            <div className="predyx7-trajectory">
              Clinical Intelligence Dashboard
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* BUILT FOR CLINICAL DECISIONS - CMS ID 31 */}
      {/* ============================================ */}
      <section className="predyx3-section" style={{ background: "#f9fcff" }}>
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[34]?.cms_page_heading || "Built for <span>Clinical Decisions</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[34]?.cms_page_small_description}
            </p>
          </div>

          <div className="predyx3-timeline mt-5">
            {parse(cms[34]?.cms_page_description || "")}
          </div>
        </div>
      </section>

    

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 32 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "28px", color: "#fff", fontWeight: "600", marginBottom: "20px" }}>
              {parse(cms[35]?.cms_page_heading || "Every test tells part of your story.<br />The PredyxIQ™ Clinical Intelligence Report helps you understand the whole story.")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {cms[35]?.cms_page_small_description}
            </p>
            <a href="#" className="action-items-primary-btn1">
              View a Sample Clinical Intelligence Report
              <i className="fas fa-chevron-right" />
            </a>
            <a href="#" className="action-items-primary-btn1">
              Start Your Quick Assessment
              <i className="fas fa-chevron-right" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainPage;